// When a tool can run: what its button's greyed-out reason says, and why an
// armed tool falls back to Select — both read from here, once, by main.ts's
// updateToolButtons. docs/claude/tools.md, *When a tool can run*. Pure.
import type { Tool } from './ui/core';

/** What the preconditions read, built once per toolbar refresh. */
export interface ToolContext {
  /** The tool armed now. */
  armed: Tool;
  /** How many objects are selected. */
  selected: number;
  /** A room's or a drawn wall's feature is selected in the section view the
   *  tools act in, where it only moves. */
  sectionFeature: boolean;
  /** The drawing has an underlay to scale: an imported PDF or image. */
  underlay: boolean;
  /** The selected object is a walled room (a sauna or a steam room). */
  room: boolean;
  /** The selected walled room is drawn as a polygon: a ceiling plan can show it. */
  polyRoom: boolean;
  /** The selected walled room's ceiling holds a cove: flat, or Coved. */
  coveCeiling: boolean;
  /** The selected object is a standalone sauna bench. */
  saunaBench: boolean;
  /** One drawn wall that takes doors, panes and openings is selected (a band,
   *  not a line). */
  bandWall: boolean;
  /** The drawing editor is open on something that is only closed outlines (a
   *  steam bench's profile): only the tools in `CLOSED_OUTLINE_TOOLS` run. */
  closedOnly: boolean;
}

/** Why a tool cannot run in `c` (English: its button's greyed-out reason), or
 *  null when it can. `armed`: it is the tool armed now. */
export type ToolNeed = (c: ToolContext, armed: boolean) => string | null;

/** In a section view a feature only moves; what else would act on it there. */
export const FEATURE_ONLY_MOVES = 'In a section view a room’s feature only moves — along its wall and up. Copy, mirror, scale, arrays and arranging take whole objects.';

const ROOM = 'Select a room first';

const inRoom: ToolNeed = (c) => (c.room ? null : ROOM);
/** A door, a pane or an opening goes in a room's wall or in a drawn wall. */
const inWall: ToolNeed = (c) => (c.room || c.bandWall ? null : 'Select a room or wall first');
/** The Bench tool, supports and bench ends act on a room's benches or a sauna bench. */
const onBench: ToolNeed = (c) => (c.room || c.saunaBench ? null : 'Select a room or a sauna bench first');
/** A reflected ceiling plan (and the plan above it) is a view of the selected
 *  walled room. */
const ofCeiling: ToolNeed = (c) => (c.room && c.polyRoom ? null : ROOM);
/** A cove needs a flat or Coved ceiling to stand in. */
const inCove: ToolNeed = (c) => (!c.room ? ROOM : c.coveCeiling ? null : 'Needs a flat or Coved ceiling');
/** A tool that takes the selection runs once something is selected, and stays
 *  runnable while it is armed: its prompt asks for the selection, its button
 *  lets it go, and it never falls back to Select. */
const onSelection: ToolNeed = (c, armed) => (c.selected > 0 || armed ? null : c.sectionFeature ? FEATURE_ONLY_MOVES : 'Select something first');
/** Move takes a room's feature selected in a section view too: it moves there. */
const moves: ToolNeed = (c, armed) => (c.sectionFeature ? null : onSelection(c, armed));
/** Scale to measure measures on an underlay. */
const onUnderlay: ToolNeed = (c) => (c.underlay ? null : 'Import a PDF or image first');

/** Each tool that cannot always run, and what it needs. A tool with no row
 *  always can. */
export const TOOL_NEEDS: Partial<Record<Tool, ToolNeed>> = {
  backrest: inRoom,
  'sculpt-bench': inRoom,
  bench: onBench,
  'bench-end': onBench,
  'bench-round': inRoom,
  'bench-support': onBench,
  'ceiling-top': ofCeiling,
  copy: onSelection,
  door: inWall,
  'floor-grille': inRoom,
  glass: inWall,
  'heater-wall': inRoom,
  'inner-wall': inRoom,
  'light-channel': inRoom,
  'light-cove': inCove,
  'light-sky': inRoom,
  'light-spot': inRoom,
  'light-strip': inRoom,
  'light-strip-up': inRoom,
  mirror: onSelection,
  move: moves,
  opening: inWall,
  'pdf-scale': onUnderlay,
  rcp: ofCeiling,
  rotate: onSelection,
};

/** The tools a closed-outline drawing editor takes: none draws an open line. */
export const CLOSED_OUTLINE_TOOLS: ReadonlySet<Tool> = new Set<Tool>([
  'polygon',
  'rectangle',
  'select',
  'zoom',
]);
/** Why the others are greyed out there. */
export const CLOSED_ONLY = 'A bench profile is one closed outline: draw it with Polygon or Rectangle';

/** Why tool `t` cannot run in `c`, or null when it can. */
export function toolReason(t: Tool, c: ToolContext): string | null {
  if (c.closedOnly && !CLOSED_OUTLINE_TOOLS.has(t)) return CLOSED_ONLY;
  return TOOL_NEEDS[t]?.(c, c.armed === t) ?? null;
}
